/**
 * UpgradeModal Component
 *
 * Modal that displays when a subscription limit is reached.
 * Listens for 'subscription-limit-reached' events from UnifiedApiService
 * and displays an upgrade prompt with the specific limit that was hit.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Modal } from '../../design-system/components/Modal/Modal';
import { Button } from '../../design-system/components/Button';
import { useNavigate } from 'react-router-dom';

// Event detail interface for type safety
interface SubscriptionLimitEvent {
  message: string;
  code?: string;
  url?: string;
}

const Content = styled.div`
  text-align: center;
  padding: var(--spacing-md) 0;
`;

const IconContainer = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto var(--spacing-lg);
  background-color: var(--color-warning-light, #fff3cd);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
`;

const Title = styled.h3`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
`;

const Message = styled.p`
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-lg);
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  text-align: left;
  padding: 0 var(--spacing-lg);
  margin: var(--spacing-lg) 0;
`;

const FeatureItem = styled.li`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);

  &::before {
    content: "\u2714";
    color: var(--color-success, #28a745);
    font-weight: bold;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-md);
  justify-content: center;
  margin-top: var(--spacing-lg);
`;

const StyledButton = styled(Button)`
  min-width: 140px;
`;

export const UpgradeModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [code, setCode] = useState('');
  const navigate = useNavigate();

  const handleSubscriptionLimit = useCallback((event: CustomEvent<SubscriptionLimitEvent>) => {
    const { message: eventMessage, code: eventCode } = event.detail;
    setMessage(eventMessage || 'You have reached your subscription limit.');
    setCode(eventCode || '');
    setIsOpen(true);
  }, []);

  useEffect(() => {
    // Listen for subscription limit events from UnifiedApiService
    window.addEventListener(
      'subscription-limit-reached',
      handleSubscriptionLimit as EventListener
    );

    return () => {
      window.removeEventListener(
        'subscription-limit-reached',
        handleSubscriptionLimit as EventListener
      );
    };
  }, [handleSubscriptionLimit]);

  const handleClose = () => {
    setIsOpen(false);
    setMessage('');
    setCode('');
  };

  const handleUpgrade = () => {
    handleClose();
    navigate('/settings/subscription');
  };

  const getLimitType = (): string => {
    switch (code) {
      case 'MEMBER_LIMIT_REACHED':
        return 'member';
      case 'STAFF_LIMIT_REACHED':
        return 'staff account';
      case 'MINISTRY_LIMIT_REACHED':
        return 'ministry';
      case 'ASSESSMENT_LIMIT_REACHED':
        return 'assessment';
      case 'STORAGE_LIMIT_REACHED':
        return 'storage';
      default:
        return 'resource';
    }
  };

  const upgradeFeatures = [
    'Increased member capacity',
    'More staff accounts',
    'Additional ministries',
    'Higher assessment limits',
    'More storage space',
    'Priority support',
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upgrade Required"
      size="small"
    >
      <Content>
        <IconContainer>
          <span role="img" aria-label="upgrade">&#128274;</span>
        </IconContainer>

        <Title>Subscription Limit Reached</Title>

        <Message>
          {message || `You've reached the ${getLimitType()} limit for your current plan.`}
        </Message>

        <FeatureList>
          {upgradeFeatures.slice(0, 4).map((feature, index) => (
            <FeatureItem key={index}>{feature}</FeatureItem>
          ))}
        </FeatureList>

        <ButtonContainer>
          <StyledButton variant="secondary" onClick={handleClose}>
            Maybe Later
          </StyledButton>
          <StyledButton variant="primary" onClick={handleUpgrade}>
            View Plans
          </StyledButton>
        </ButtonContainer>
      </Content>
    </Modal>
  );
};

export default UpgradeModal;
